/**
 * Кнопка «Скопировать данные доступа» — формирует готовое сообщение
 * для пересылки получателю в Telegram/WhatsApp/email.
 *
 * Стратегия (10/10 UX):
 *
 * - Primary (большая кнопка): универсальное сообщение со ссылками на
 *   HAPP (рекомендуется) + Karing (альтернатива), для iPhone и Android,
 *   плюс ключ и пошаговая инструкция.
 *
 * - Secondary («Для iPhone» / «Для Android»): готовое сообщение
 *   адаптированное под конкретную платформу — без ссылок на чужой
 *   App Store, только то что получателю реально нужно.
 *
 * - Tertiary («Только ключ»): только sub-URL — для случая когда
 *   получатель уже установил приложение.
 *
 * После копирования — анимированный фидбэк (зелёная кнопка с галочкой
 * на 2.4 сек) и notification.
 */
import { useState } from "react";
import { Button, Space, message, Typography, Card, Tooltip } from "antd";
import {
  CopyOutlined, CheckCircleOutlined,
  AppleOutlined, AndroidOutlined, KeyOutlined,
} from "@ant-design/icons";

// — клиенты —
const HAPP_IOS_URL    = "https://apps.apple.com/app/happ-proxy-utility/id6504287215";
const HAPP_ANDROID_URL = "https://play.google.com/store/apps/details?id=com.happproxy";
const KARING_IOS_URL  = "https://apps.apple.com/app/karing/id6472431552";
const KARING_ANDROID_URL = "https://karing.app/";

const SUPPORT_HANDLE = "@bemliBot";

interface Props {
  subUrl: string;
  brand?: string;
}

const SEP = "━━━━━━━━━━━━━━━━━━━━━━━";

const buildFull = (subUrl: string, brand: string) =>
`🔐 Ваш VPN-доступ от ${brand}
${SEP}

📱 ШАГ 1. Установите приложение

✅ Рекомендуем HAPP (создан специально для нашего протокола):
   • iPhone:  ${HAPP_IOS_URL}
   • Android: ${HAPP_ANDROID_URL}

🔄 Если HAPP недоступен в вашем App Store — поставьте Karing:
   • iPhone:  ${KARING_IOS_URL}
   • Android: ${KARING_ANDROID_URL}

${SEP}

🔑 ШАГ 2. Скопируйте ключ доступа (одной строкой):

${subUrl}

${SEP}

▶️ ШАГ 3. Импортируйте профиль

В HAPP:  «+» сверху справа → «Add from clipboard» (Из буфера)
В Karing: «+» снизу → «Add from clipboard»

${SEP}

📵 ШАГ 4 (ОБЯЗАТЕЛЬНО для Android, на iOS — пропустите)

В Android по умолчанию включён Mux — он несовместим с нашим
протоколом, и без его отключения подключение будет падать
с ошибкой «TLS handshake error».

   HAPP:     Profile → Edit → Advanced → Mux: OFF
   v2rayNG:  Profile → Edit → выключить «Mux Enabled»
   NekoBox:  Settings → Profile → выключить Mux

Это разовая настройка. На iOS Mux уже выключен — там этот шаг
делать не нужно.

${SEP}

🧩 ШАГ 5 (ОБЯЗАТЕЛЬНО для Android, на iOS — пропустите)

Выключите глобальную настройку «Enable Fragmentation» —
с нашим протоколом она не нужна и режет скорость до 1-3 Мбит/с
(VPN подключается, но всё работает крайне медленно).

   HAPP:     Settings → Tunnel Settings → Enable Fragmentation: OFF

Это глобальный флаг приложения, не настройка профиля. Один раз
выключили — больше не возвращается.

${SEP}

▶️ ШАГ 6. Подключение

Нажмите Connect (или кнопку запуска) — готово ✅

${SEP}

❓ Не работает или есть вопросы — напишите в поддержку ${SUPPORT_HANDLE}, мы поможем.`;

const buildIos = (subUrl: string, brand: string) =>
`🔐 Ваш VPN-доступ от ${brand} (для iPhone)
${SEP}

📱 ШАГ 1. Установите приложение HAPP (рекомендуется):
   ${HAPP_IOS_URL}

   Если HAPP недоступен в App Store — поставьте Karing:
   ${KARING_IOS_URL}

${SEP}

🔑 ШАГ 2. Скопируйте ключ доступа (одной строкой):

${subUrl}

${SEP}

▶️ ШАГ 3. В приложении: «+» → «Add from clipboard»
         (или «Из буфера обмена») → нажмите Connect.

Готово ✅ — на iPhone никаких дополнительных настроек не нужно.

${SEP}

❓ Не работает — напишите ${SUPPORT_HANDLE}`;

const buildAndroid = (subUrl: string, brand: string) =>
`🔐 Ваш VPN-доступ от ${brand} (для Android)
${SEP}

📱 ШАГ 1. Установите приложение HAPP (рекомендуется):
   ${HAPP_ANDROID_URL}

   Альтернатива — Karing:
   ${KARING_ANDROID_URL}

${SEP}

🔑 ШАГ 2. Скопируйте ключ доступа (одной строкой):

${subUrl}

${SEP}

▶️ ШАГ 3. Импортируйте в HAPP / Karing:
   «+» → «Add from clipboard» (или «Из буфера обмена»)

${SEP}

📵 ШАГ 4 (ОБЯЗАТЕЛЬНО — иначе не заработает)

В Android по умолчанию включён Mux (мультиплексирование). С нашим
протоколом он несовместим — без его отключения подключение
падает с ошибкой «TLS handshake error» или есть подключение,
но нет интернета.

Откройте созданный профиль и отключите Mux:
   HAPP:     Profile → Edit → Advanced → Mux: OFF
   v2rayNG:  Profile → Edit → выключить «Mux Enabled»
   NekoBox:  Settings → Profile → выключить Mux

Это разовая настройка — потом всё работает стабильно.

${SEP}

🧩 ШАГ 5 (ОБЯЗАТЕЛЬНО — без этого скорость 1-3 Мбит/с)

Выключите глобальный флаг «Enable Fragmentation». Это анти-DPI
обфускация для других протоколов; с нашим протоколом она не нужна
и наоборот режет скорость до 1-3 Мбит/с (VPN работает, но крайне
медленно — это не интернет провайдера, это именно эта настройка).

   HAPP:     Settings → Tunnel Settings → Enable Fragmentation: OFF

Это не настройка профиля, а глобальный тумблер всего приложения.
Один раз выключили — больше не возвращается.

${SEP}

▶️ ШАГ 6. Нажмите Connect — готово ✅

${SEP}

❓ Не работает — напишите ${SUPPORT_HANDLE}`;

export const CopyAccessButton = ({ subUrl, brand = "Bemli" }: Props) => {
  const [copiedAll, setCopiedAll] = useState(false);

  const copy = (text: string, label: string, isPrimary = false) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        message.success(`Скопировано: ${label}`);
        if (isPrimary) {
          setCopiedAll(true);
          setTimeout(() => setCopiedAll(false), 2400);
        }
      })
      .catch(() => message.error("Ошибка копирования"));
  };

  return (
    <Card
      size="small"
      style={{ background: "#f0f5ff", borderColor: "#1677ff" }}
      bodyStyle={{ padding: 16 }}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Typography.Text strong style={{ fontSize: 15 }}>
            📤 Готовое сообщение для получателя
          </Typography.Text>
          <Typography.Paragraph type="secondary"
            style={{ fontSize: 12, margin: "4px 0 0" }}>
            Нажмите «Скопировать всё» — готовый текст с инструкцией, ссылками на
            приложения для iPhone и Android, и ключом улетит в буфер.
            Просто перешлите получателю в Telegram/WhatsApp — и он сразу подключится.
          </Typography.Paragraph>
        </div>

        <Button
          type="primary"
          size="large"
          block
          icon={copiedAll ? <CheckCircleOutlined /> : <CopyOutlined />}
          onClick={() =>
            copy(buildFull(subUrl, brand), "полное сообщение", true)
          }
          style={{
            height: 60, fontSize: 16, fontWeight: 600,
            background: copiedAll ? "#52c41a" : undefined,
            borderColor: copiedAll ? "#52c41a" : undefined,
            transition: "all .25s",
          }}
        >
          {copiedAll
            ? "✓ Скопировано — пересылайте получателю"
            : "📋 Скопировать данные доступа (всё)"}
        </Button>

        <div style={{ display: "flex", gap: 8 }}>
          <Tooltip title="Сообщение со ссылками на HAPP/Karing для iPhone + ключ + инструкция">
            <Button
              size="middle" block icon={<AppleOutlined />}
              onClick={() => copy(buildIos(subUrl, brand),
                                  "сообщение для iPhone")}
            >
              Для iPhone
            </Button>
          </Tooltip>
          <Tooltip title="Сообщение со ссылками на HAPP/Karing для Android + ключ + инструкция">
            <Button
              size="middle" block icon={<AndroidOutlined />}
              onClick={() => copy(buildAndroid(subUrl, brand),
                                  "сообщение для Android")}
            >
              Для Android
            </Button>
          </Tooltip>
          <Tooltip title="Только ключ доступа — для тех у кого приложение уже установлено">
            <Button
              size="middle" block icon={<KeyOutlined />}
              onClick={() => copy(subUrl, "ключ доступа")}
            >
              Только ключ
            </Button>
          </Tooltip>
        </div>

        <details>
          <summary style={{ cursor: "pointer", fontSize: 12,
                            color: "#666", userSelect: "none",
                            padding: "4px 0" }}>
            Показать предпросмотр полного сообщения
          </summary>
          <Typography.Paragraph
            style={{
              fontSize: 12, marginTop: 8, marginBottom: 0,
              padding: 12, background: "#fff", borderRadius: 4,
              border: "1px solid #d9d9d9",
              whiteSpace: "pre-wrap", fontFamily: "inherit",
              maxHeight: 400, overflow: "auto",
            }}>
            {buildFull(subUrl, brand)}
          </Typography.Paragraph>
        </details>
      </Space>
    </Card>
  );
};
